<template>
  <div class="editor-container">
    <Editor
      :model-value="inputText"
      @update:modelValue="onEditorChange"
      :init="configEditor"
      :key="editorKey"
    />
    <p
      v-if="isPublicationProfile && validation.message"
      class="editor-container__validation"
      role="alert"
    >
      {{ validation.message }}
    </p>
  </div>
</template>

<script>
import Editor from '@tinymce/tinymce-vue'
import 'tinymce/tinymce'
import 'tinymce/themes/silver'
import 'tinymce/icons/default'
import 'tinymce/models/dom'
import 'tinymce/plugins/lists'
import 'tinymce/plugins/link'
import 'tinymce/plugins/code'
import 'tinymce/plugins/image'
import 'tinymce/plugins/autoresize'
import 'tinymce/plugins/table'
import 'tinymce/plugins/fullscreen'
import 'tinymce/plugins/preview'
import {getUserAuthToken} from '@/middleware/auth'
import {
  buildUploadedImagePresentation,
  validatePublicationHtml,
} from '@/utils/rich-content'

const DEFAULT_CONTENT_STYLE = `body {margin: 0; padding: 8px; width: 100%;} body * {font-size: 14px; font-family: 'FiraSans', sans-serif !important; color: black;} body a {color: #364C3A; text-decoration: underline; text-underline-offset: 4px;}`

const PUBLICATION_CONTENT_STYLE = `
  body { margin: 0; padding: 24px; width: 100%; box-sizing: border-box; font-family: 'FiraSans', sans-serif; color: #183C2E; }
  p, li { font-size: 16px; font-weight: 300; line-height: 1.65; }
  h2 { margin: 32px 0 16px; font-size: 28px; line-height: 1.25; }
  h3 { margin: 28px 0 14px; font-size: 23px; line-height: 1.3; }
  h4 { margin: 24px 0 12px; font-size: 19px; line-height: 1.35; }
  blockquote { margin: 24px 0; border-left: 4px solid #9CB7A2; padding: 12px 20px; background: #F4F8F4; }
  a { color: #364C3A; text-decoration: underline; text-underline-offset: 4px; }
  figure.image { margin: 32px auto; text-align: center; }
  figure.image img, img.content-image { display: block; width: auto; max-width: min(100%, 820px); max-height: min(56vh, 820px); height: auto; margin: 32px auto; border-radius: 14px; object-fit: contain; }
  figure.image img { margin: 0 auto; }
  figure.image figcaption { margin-top: 10px; color: #617469; font-size: 14px; line-height: 1.45; }
  figure.image img.content-image--compact, img.content-image--compact { max-width: min(100%, 640px); max-height: min(48vh, 640px); }
  figure.image img.content-image--full, img.content-image--full { max-width: 100%; max-height: min(72vh, 820px); }
  table.content-table { width: 100%; min-width: max-content; margin: 28px 0; border-collapse: collapse; table-layout: auto; font-size: 15px; }
  table.content-table caption { margin-bottom: 10px; color: #617469; font-size: 14px; text-align: left; }
  table.content-table th, table.content-table td { border: 1px solid #D8E2DA; padding: 12px; vertical-align: top; white-space: nowrap; }
  table.content-table th { background: #EEF4EF; font-weight: 500; text-align: left; }
  table.content-table--striped tbody tr:nth-child(even) { background: #F7FAF7; }
`

export default {
  name: "EditorContainer",
  props: {
    activeItem: {
      required: true
    },
    profile: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'publication'].includes(value),
    }
  },
  components: {Editor},
  data() {
    return {
      inputText: '',
      activeSkin: 'oxide',
      editorKey: 0,
      colorText: '',
      editorInstance: null,
      pendingImageMetadata: new Map(),
      validation: {valid: true, imagesWithoutAlt: 0, message: ''},
      configEditor: null,
    }
  },
  computed: {
    isPublicationProfile() {
      return this.profile === 'publication'
    },
  },
  created() {
    this.configEditor = this.createEditorConfig()
  },
  methods: {
    createEditorConfig() {
      const publicationConfig = this.isPublicationProfile ? {
        min_height: 560,
        max_height: 1000,
        content_style: PUBLICATION_CONTENT_STYLE,
        plugins: 'lists link code image table fullscreen preview autoresize',
        toolbar: 'undo redo | blocks | bold italic underline | bullist numlist blockquote | link image table | alignleft aligncenter alignright | removeformat code preview fullscreen | b24form',
        toolbar_mode: 'wrap',
        block_formats: 'Абзац=p; Заголовок 2=h2; Заголовок 3=h3; Заголовок 4=h4',
        formats: {
          alignleft: {selector: 'p,h2,h3,h4', classes: 'content-align-left'},
          aligncenter: {selector: 'p,h2,h3,h4', classes: 'content-align-center'},
          alignright: {selector: 'p,h2,h3,h4', classes: 'content-align-right'},
        },
        image_advtab: false,
        image_caption: true,
        image_title: true,
        image_description: true,
        image_dimensions: false,
        image_class_list: [
          {title: 'Стандартное (рекомендуется)', value: 'content-image'},
          {title: 'Компактное', value: 'content-image content-image--compact'},
          {title: 'На всю ширину', value: 'content-image content-image--full'},
        ],
        table_style_by_css: true,
        table_sizing_mode: 'responsive',
        table_appearance_options: false,
        table_default_attributes: {class: 'content-table'},
        table_default_styles: {},
        table_class_list: [
          {title: 'Обычная', value: 'content-table'},
          {title: 'С чередованием строк', value: 'content-table content-table--striped'},
        ],
        paste_data_images: true,
        images_file_types: 'jpeg,jpg,png,webp',
        images_reuse_filename: false,
        object_resizing: false,
        a11y_advanced_options: true,
        convert_urls: false,
      } : {}

      return {
        base_url: '/tinymce',
        suffix: '.min',
        width: '100%',
        min_height: 300,
        max_height: 800,
        menubar: false,
        resize: false,
        content_style: DEFAULT_CONTENT_STYLE,
        plugins: 'lists link code image autoresize',
        toolbar: 'undo redo | styles | forecolor | bold italic underline strikethrough | code removeformat | bullist numlist | link image | alignleft aligncenter alignright alignjustify | b24form',
        extended_valid_elements: 'a[data-b24-form|href|target|class|rel]',
        image_advtab: true,
        file_picker_types: 'image',
        automatic_uploads: true,
        images_upload_handler: (blobInfo, progress) => this.uploadImage(blobInfo, progress),
        setup: (editor) => this.setupEditor(editor),
        ...publicationConfig,
      }
    },
    uploadImage(blobInfo, progress) {
      return new Promise((resolve, reject) => {
        const authToken = getUserAuthToken()
        if (!authToken) {
          reject('Сессия истекла. Войдите в админку повторно.')
          return
        }

        const formData = new FormData()
        formData.append('uploadedFile', blobInfo.blob(), blobInfo.filename())

        const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:3000'
        const filesUrl = process.env.VUE_APP_BUILD_ENV || 'http://localhost:9000/nalogika'
        const xhr = new XMLHttpRequest()
        xhr.open('POST', `${apiUrl}/file/tinymce-upload`, true)
        xhr.setRequestHeader('Authorization', `Bearer ${authToken}`)
        xhr.timeout = 60000

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable && typeof progress === 'function') {
            progress(Math.round((event.loaded / event.total) * 100))
          }
        }

        xhr.onload = () => {
          if (xhr.status === 200 || xhr.status === 201) {
            try {
              const presentation = buildUploadedImagePresentation(JSON.parse(xhr.responseText), filesUrl)
              this.pendingImageMetadata.set(presentation.src, presentation)
              this.applyPendingImageMetadata()
              resolve(presentation.src)
              window.setTimeout(() => this.applyPendingImageMetadata(), 0)
              window.setTimeout(() => this.applyPendingImageMetadata(), 250)
            } catch (error) {
              reject(`Ошибка обработки изображения: ${error.message}`)
            }
            return
          }

          if (xhr.status === 401 || xhr.status === 403) {
            reject('Недостаточно прав или сессия истекла.')
            return
          }

          if (xhr.status === 413) {
            reject('Изображение превышает 10 МБ.')
            return
          }

          reject(`Не удалось загрузить изображение (HTTP ${xhr.status}).`)
        }

        xhr.onerror = () => reject('Ошибка сети при загрузке изображения.')
        xhr.ontimeout = () => reject('Загрузка изображения превысила 60 секунд.')
        xhr.send(formData)
      })
    },
    setupEditor(editor) {
      this.editorInstance = editor
      editor.ui.registry.addButton('b24form', {
        text: 'Форма B24',
        onAction: () => {
          editor.insertContent('<a href="#" data-b24-form="click/224/i3f23y">Оставить заявку</a>')
        }
      })
      editor.on('init', () => {
        if (this.activeItem) editor.setContent(this.activeItem)
        this.validateContent(editor.getContent())
      })
      editor.on('NodeChange SetContent', () => this.applyPendingImageMetadata())
    },
    applyPendingImageMetadata() {
      if (!this.editorInstance || !this.pendingImageMetadata.size) return

      let contentChanged = false
      this.editorInstance.dom.select('img').forEach((image) => {
        const src = this.editorInstance.dom.getAttrib(image, 'src')
        const presentation = this.pendingImageMetadata.get(src)
        if (!presentation) return

        this.pendingImageMetadata.delete(src)
        const attributes = {
          class: this.editorInstance.dom.getAttrib(image, 'class') || 'content-image',
          loading: 'lazy',
          decoding: 'async',
          sizes: presentation.sizes,
        }
        if (presentation.srcset) attributes.srcset = presentation.srcset
        if (presentation.width) attributes.width = String(presentation.width)
        if (presentation.height) attributes.height = String(presentation.height)
        this.editorInstance.dom.setAttribs(image, attributes)
        contentChanged = true
      })

      if (contentChanged) this.onEditorChange(this.editorInstance.getContent())
    },
    validateContent(content) {
      this.validation = this.isPublicationProfile
        ? validatePublicationHtml(content)
        : {valid: true, imagesWithoutAlt: 0, message: ''}
      this.$emit('contentValidation', this.validation)
    },
    onEditorChange(content) {
      this.inputText = content;
      this.validateContent(content)
      this.$emit('changeEditorContent', content);
    }
  },
  mounted() {
    if (this.activeItem) {
      this.inputText = this.activeItem;
    }
  },
  watch: {
    activeItem(newVal) {
      if (newVal !== this.inputText) {
        this.inputText = newVal;
        this.validateContent(newVal)
      }
    },
  }
}
</script>

<style lang="scss">
.editor-container {
  width: 100%;
  border: 1px solid rgba(var(--color-grey-rgba), 0.6);
  border-radius: 8px;
  overflow: hidden;

  &:focus-within {
    border-color: var(--color-dark-green);
  }

  &__validation {
    margin: 0;
    border-top: 1px solid #d7a09b;
    padding: 10px 14px;
    color: #8f2f25;
    font-size: 14px;
    background: #fff5f4;
  }

  // TinyMCE specific styles
  :deep(.tox-tinymce) {
    border: none;
    border-radius: 8px;
    width: 100% !important;
  }

  :deep(.tox-editor-header) {
    border-bottom: 1px solid rgba(var(--color-grey-rgba), 0.3);
  }

  :deep(.tox-toolbar) {
    background: #f8f9fa;
  }

  :deep(.tox-edit-area) {
    width: 100% !important;
  }

  :deep(.tox-edit-area__iframe) {
    width: 100% !important;
  }
}
</style>
